import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { deductionsApi } from '../api/deductionsApi';
import { appToast } from '../../../../components/AppToast';

const DEFAULT_FORM_STATE = {
  emp_id: '',
  emp_name: '',
  date: new Date().toISOString().slice(0, 10),
  pay_head_id: '',
  amount: '',
  vno: null,
};

export function useDeductionsEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(DEFAULT_FORM_STATE);
  const [dynamicOptions, setDynamicOptions] = useState({
    employees: [],
    payHeads: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load dynamic options (active employees and deduction payheads)
  const loadOptions = useCallback(async () => {
    try {
      const opts = await deductionsApi.getDeductionOptions();
      setDynamicOptions(opts);
      return opts;
    } catch (err) {
      console.error('Failed to load deduction options:', err);
      appToast.error('Failed to load options for employee deductions.');
      return { employees: [], payHeads: [] };
    }
  }, []);

  // Load existing deduction record when in edit mode
  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      const record = await deductionsApi.getDeductionById(id);
      if (record) {
        setForm({
          emp_id: record.emp_id || '',
          emp_name: record.employee || '',
          date: record.vDate || new Date().toISOString().slice(0, 10),
          pay_head_id: record.pay_head_id || '',
          amount: record.amount ?? '',
          vno: record.vnoRaw || null,
        });
      } else {
        appToast.error('Deduction record not found.');
        navigate('/payroll/deductions');
      }
    } catch (err) {
      appToast.error('Failed to load deduction details.');
      navigate('/payroll/deductions');
    }
  }, [id, navigate]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    Promise.all([loadOptions(), isEdit ? loadData() : Promise.resolve()])
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [loadOptions, loadData, isEdit]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async ({ exitAfter = true } = {}) => {
    // Exact legacy BLL validations
    if (!form.emp_id || Number(form.emp_id) === 0) {
      appToast.error('Please Select Employee Name');
      return;
    }
    if (!form.pay_head_id || Number(form.pay_head_id) === 0) {
      appToast.error('Please Select PayHead');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      appToast.error('Please enter a valid amount');
      return;
    }
    if (!form.date) {
      appToast.error('Please select a voucher date.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        emp_id: Number(form.emp_id),
        pay_head_id: Number(form.pay_head_id),
        amount: Number(form.amount),
        v_date: form.date,
      };

      if (isEdit) {
        await deductionsApi.updateDeduction(id, payload);
        appToast.success('Deduction updated successfully!');
      } else {
        await deductionsApi.createDeduction(payload);
        appToast.success('Deduction recorded successfully!');
      }

      if (exitAfter) {
        navigate('/payroll/deductions');
      } else if (!isEdit) {
        setForm({
          ...DEFAULT_FORM_STATE,
          date: form.date, // maintain date for rapid repeated entries
        });
      }
    } catch (err) {
      const errMsg = err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Failed to save deduction.';
      appToast.error(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      form,
      isEdit,
      editId: id,
      isLoading,
      isSaving,
      dynamicOptions,
    },
    actions: {
      patchForm,
      handleSave,
    },
  };
}
